function mapOptions(record) {
    if (!record || typeof record !== 'object') {
        return [];
    }
    return Object.entries(record).map(([value, label]) => ({
        value: String(value),
        label: String(label),
    }));
}

function mapMergeTargets(record) {
    if (!record || typeof record !== 'object') {
        return [];
    }

    return Object.entries(record).map(([value, item]) => {
        if (item && typeof item === 'object' && !Array.isArray(item)) {
            return {
                value: String(value),
                label: String(item.label ?? ''),
                assigned_user_id: item.assigned_user_id ?? null,
                assigned_user_label: item.assigned_user_label ?? '',
                customer_email: String(item.customer_email ?? ''),
                needs_confirmation: Boolean(item.needs_confirmation),
                mismatch_reasons: Array.isArray(item.mismatch_reasons) ? item.mismatch_reasons : [],
            };
        }

        return {
            value: String(value),
            label: String(item),
            assigned_user_id: null,
            assigned_user_label: '',
            customer_email: '',
            needs_confirmation: false,
            mismatch_reasons: [],
        };
    });
}

function mapAdminLteBadgeToDesk(adminLteClass) {
    if (adminLteClass === 'label-success') {
        return 'success';
    }
    if (adminLteClass === 'label-danger') {
        return 'danger';
    }
    if (adminLteClass === 'label-warning') {
        return 'warning';
    }
    if (adminLteClass === 'label-default' || adminLteClass === 'label-info') {
        return 'neutral';
    }
    return adminLteClass ? 'neutral' : null;
}

/**
 * Prefer PHP-flattened ticket fields; fall back to context.show_panels customer-rating.
 *
 * @param {Record<string, unknown>} raw
 * @returns {Record<string, string|null>}
 */
const KNOWN_RATING_TIERS = new Set(['top', 'basic', 'dangerous']);

function knownRatingTier(tier, tierLabel) {
    if (!tier || !tierLabel || !KNOWN_RATING_TIERS.has(String(tier).toLowerCase())) {
        return {
            customer_rating_tier: null,
            customer_rating_tier_label: null,
            customer_rating_badge: null,
        };
    }
    return null;
}

function customerRatingFields(raw) {
    const ticket = raw.ticket || {};
    // PHP always emits these keys (null when unknown) — trust flatten, do not rehydrate from panel.
    if (Object.prototype.hasOwnProperty.call(ticket, 'customer_rating_tier_label')
        || Object.prototype.hasOwnProperty.call(ticket, 'customer_rating_tier')) {
        const blocked = knownRatingTier(ticket.customer_rating_tier, ticket.customer_rating_tier_label);
        if (blocked) {
            return blocked;
        }

        return {
            customer_rating_tier: ticket.customer_rating_tier ?? null,
            customer_rating_tier_label: ticket.customer_rating_tier_label ?? null,
            customer_rating_badge: ticket.customer_rating_badge ?? null,
        };
    }

    const panels = raw.context?.show_panels || [];
    const panel = panels.find((row) => row && row.id === 'customer-rating');
    if (!panel?.data) {
        return {
            customer_rating_tier: null,
            customer_rating_tier_label: null,
            customer_rating_badge: null,
        };
    }

    const tier = panel.data.customer_rating?.tier ?? null;
    const tierLabel = panel.data.tier_label ?? null;
    const blocked = knownRatingTier(tier, tierLabel);
    if (blocked) {
        return blocked;
    }

    return {
        customer_rating_tier: tier,
        customer_rating_tier_label: tierLabel,
        customer_rating_badge: mapAdminLteBadgeToDesk(panel.data.badge_class),
    };
}

/**
 * Maps BackBeat TicketDeskIslandPayloadBuilder JSON to Lovable vue-desk-export shape.
 *
 * @param {Record<string, unknown>} raw
 * @returns {Record<string, unknown>}
 */
export function normalizeDeskPayload(raw) {
    if (!raw || typeof raw !== 'object') {
        return {};
    }

    const tb = raw.desk_toolbar || {};
    const ticket = raw.ticket || {};
    const composer = raw.composer || {};
    const statuses = tb.operational_statuses || {};
    const statusKey = ticket.status;
    const pendingDraft = composer.pending_ai_draft;
    const draftBodyText = pendingDraft?.body_text != null ? String(pendingDraft.body_text) : '';
    const aiDraftBody = composer.ai_draft_body != null ? String(composer.ai_draft_body) : '';
    const replyBody = composer.reply_body != null ? String(composer.reply_body) : '';
    const composerText = pendingDraft || aiDraftBody
        ? (draftBodyText && replyBody === draftBodyText ? '' : (composer.text ?? ''))
        : (composer.text ?? composer.reply_body ?? '');

    const assignees = [{ value: '', label: '—' }, ...mapOptions(tb.assigned_user_options)];

    return {
        ...raw,
        desk_toolbar: {
            ...tb,
            assignee: tb.assigned_user_id != null ? String(tb.assigned_user_id) : '',
            assignees,
            priorities: mapOptions(tb.priority_options),
            merge_targets: mapMergeTargets(
                tb.merge_target_details && typeof tb.merge_target_details === 'object'
                    ? tb.merge_target_details
                    : tb.merge_target_options
            ),
        },
        ticket: {
            ...ticket,
            status_label: statuses[statusKey] || ticket.status_label || statusKey,
            ...customerRatingFields(raw),
        },
        composer: {
            ...composer,
            text: composerText,
            ai_draft_body: draftBodyText || composer.ai_draft_body || '',
            message_type: composer.message_type === 'internal_note' ? 'note' : 'reply',
            ai_draft: Boolean(pendingDraft || composer.ai_draft || composer.ai_draft_body),
            can_discard: Boolean(pendingDraft || composer.can_discard),
            can_clear_ui: Boolean(pendingDraft || composer.can_clear_ui || composer.ai_draft_body),
        },
        thread: {
            ...(raw.thread || {}),
            messages: (raw.thread?.messages || []).map((message) => ({
                ...message,
                sender_name: message.label,
                inbound: message.direction === 'inbound',
                actor_role: message.actor_role || (message.direction === 'inbound' ? 'customer' : 'operator'),
            })),
        },
        context: {
            ...(raw.context || {}),
            linked_orders: (raw.context?.linked_orders || []).map((order, index) => ({
                id: index,
                number: order.order_ref,
                order_ref: order.order_ref,
                label: order.order_ref,
                status_label: order.source,
                source: order.source,
            })),
            operational_notes: (raw.context?.operational_notes || []).map((note) => ({
                id: note.id,
                title: note.title,
                body: note.body,
                url: note.url || note.show_url || null,
                product_code: note.product_code,
                scope_label: note.scope_label,
            })),
        },
    };
}
